import React, { useState, useEffect, useCallback } from "react";
import { Button, Form } from "react-bootstrap";
import {
  GetUserGroupOutput,
  SetUserGroupParentInput,
  UserGroup,
} from "../../api/usergroup";
import { Loading } from "../../components";
import { anyGranted } from "../../user/user";
import UserGroupSelection, {
  loadUserGroupOptions,
  UserGroupOption,
} from "./UserGroupSelection";

// Only groups that are not the group itself, and are not child groups, can be
// a parent: hierarchies are one level deep.
export const parentOptionsFor = (
  groupId: string,
  loadOptions: (inputValue: string) => Promise<UserGroupOption[]>
) => async (inputValue: string): Promise<UserGroupOption[]> => {
  const options = await loadOptions(inputValue);
  return options.filter((o) => o.value !== groupId && !o.data?.parentID);
};

interface EditUserGroupHierarchyProps {
  groupId: string;
  getUserGroup: (
    id: string
  ) => Promise<GetUserGroupOutput> | GetUserGroupOutput;
  setUserGroupParent: (
    input: SetUserGroupParentInput
  ) => Promise<UserGroup> | UserGroup;
  // called after the parent of the group has changed
  onChanged?: () => void;
  // open another group, e.g. one of the child groups
  onEditGroup?: (id: string) => void;
  canEdit?: () => boolean;
  loadOptions?: (inputValue: string) => Promise<UserGroupOption[]>;
}

const EditUserGroupHierarchy = ({
  groupId,
  getUserGroup,
  setUserGroupParent,
  onChanged,
  onEditGroup,
  canEdit = (): boolean => anyGranted("group_update"),
  loadOptions = loadUserGroupOptions,
}: EditUserGroupHierarchyProps): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [group, setGroup] = useState<UserGroup | undefined>(undefined);
  // the current parent, and the parent picked but not yet saved
  const [parent, setParent] = useState<UserGroupOption | undefined>(undefined);
  const [selected, setSelected] = useState<UserGroupOption | undefined>(
    undefined
  );

  // load the group, and the name of its parent
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      setLoading(true);
      try {
        const g = (await getUserGroup(groupId)).UserGroup;
        let p: UserGroupOption | undefined = undefined;
        if (g?.parentID) {
          const parentGroup = (await getUserGroup(g.parentID)).UserGroup;
          p = { value: g.parentID, label: parentGroup?.name || g.parentID };
        }
        if (cancelled) return;
        setGroup(g);
        setParent(p);
        setSelected(p);
      } catch (e) {
        console.log(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return (): void => {
      cancelled = true;
    };
  }, [groupId, getUserGroup]);

  const save = useCallback(
    async (newParent?: UserGroupOption) => {
      setSaving(true);
      try {
        // the group may have been saved elsewhere on the page since it was loaded
        const current = (await getUserGroup(groupId)).UserGroup;
        if (!current) {
          throw new Error("User group not found");
        }
        const updated = await setUserGroupParent({
          id: groupId,
          revision: current.revision,
          parentID: newParent?.value,
        });
        setGroup(updated);
        setParent(newParent);
        setSelected(newParent);
        onChanged?.();
      } catch (e) {
        console.log(e);
        alert(e.message);
      } finally {
        setSaving(false);
      }
    },
    [groupId, getUserGroup, setUserGroupParent, onChanged]
  );
  const handleSetParent = useCallback(() => save(selected), [save, selected]);
  const handleClearParent = useCallback(() => save(undefined), [save]);

  if (loading) {
    return <Loading />;
  }
  if (!group) {
    return <div>User group not found</div>;
  }

  const children = group.children || [];
  const editable = canEdit() && !saving;

  // a group with child groups cannot have a parent
  if (children.length > 0) {
    return (
      <div data-testid="group-children">
        <p>
          Members of this group can see the orders assigned to its child groups.
          A group with child groups cannot be given a parent.
        </p>
        <ul>
          {children.map((c) => (
            <li key={c.id}>
              {onEditGroup ? (
                <Button
                  variant="link"
                  className="p-0"
                  onClick={(): void => onEditGroup(c.id)}
                >
                  {c.name}
                </Button>
              ) : (
                c.name
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div data-testid="group-parent">
      <Form.Group>
        <Form.Label>Parent group</Form.Label>
        <UserGroupSelection
          userGroupOption={selected}
          onChange={setSelected}
          disabled={!editable}
          loadOptions={parentOptionsFor(groupId, loadOptions)}
        />
        <Form.Text>
          Members of the parent group can see the orders assigned to this group.
          Members of this group see only its own orders.
        </Form.Text>
      </Form.Group>
      <Button
        variant="primary"
        data-testid="set-parent"
        disabled={!editable || !selected || selected.value === parent?.value}
        onClick={handleSetParent}
      >
        Set parent
      </Button>{" "}
      <Button
        variant="secondary"
        data-testid="clear-parent"
        disabled={!editable || !parent}
        onClick={handleClearParent}
      >
        Clear parent
      </Button>
    </div>
  );
};

export default EditUserGroupHierarchy;
